import React, { useCallback, useEffect, useRef } from 'react';
import { animate, createTimeline } from 'animejs';
import { SIMPL_LOGO_SVG } from '../assets/simplLogoSvg';
import { prefersReducedMotion } from '../utils/motion';

interface LoadingScreenProps {
  onComplete: () => void;
}

/**
 * Premium dark intro. A soft glow breathes behind the Simpl mark as it settles in,
 * a slim progress bar fills from the center outward, the mark gives a tiny "loaded"
 * pop, then the whole stage lifts away. Deliberately no horizontal light sweeps.
 */
const LoadingScreen: React.FC<LoadingScreenProps> = ({ onComplete }) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const glowWrapRef = useRef<HTMLDivElement>(null);
  const glowPulseRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const doneRef = useRef(false);

  // Force the wordmark white for the dark backdrop (asset ships black).
  const whiteLogo = SIMPL_LOGO_SVG.replace(/#000000/g, '#ffffff');

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    const overlay = overlayRef.current;
    const stage = stageRef.current;
    const logo = logoRef.current;
    const glowWrap = glowWrapRef.current;
    const glowPulse = glowPulseRef.current;
    const track = trackRef.current;
    const fill = fillRef.current;

    if (prefersReducedMotion() || !overlay || !stage || !logo || !fill) {
      const t = window.setTimeout(finish, 450);
      return () => window.clearTimeout(t);
    }

    // Gentle, continuous breathing glow (its own element so the timeline can fade the wrapper).
    const breathe =
      glowPulse &&
      animate(glowPulse, {
        scale: [1, 1.09],
        opacity: [0.85, 1],
        duration: 1800,
        ease: 'inOutSine',
        loop: true,
        alternate: true,
      });

    const tl = createTimeline({ defaults: { ease: 'outCubic' }, onComplete: finish });

    if (glowWrap) tl.add(glowWrap, { opacity: [0, 1], duration: 700 }, 0);
    tl.add(logo, { opacity: [0, 1], scale: [0.92, 1], translateY: [16, 0], duration: 820 }, 80);
    if (track) tl.add(track, { opacity: [0, 1], duration: 400 }, 400);
    // progress fills from the center outward (symmetric — not a left-to-right sweep)
    tl.add(fill, { scaleX: [0, 1], duration: 1300, ease: 'inOutQuart' }, 450);
    // tiny "loaded" pop on the mark
    tl.add(logo, { scale: [1, 1.035, 1], duration: 460, ease: 'inOutSine' }, 1680);

    // Lift away.
    tl.add(stage, { scale: [1, 1.06], opacity: [1, 0], duration: 680, ease: 'inCubic' }, 2200);
    tl.add(overlay, { opacity: [1, 0], duration: 620, ease: 'inQuad' }, 2320);

    return () => {
      tl.pause();
      tl.revert();
      if (breathe) breathe.pause();
    };
  }, [finish]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0a1120]"
      aria-label="Loading Simpl Processing"
    >
      {/* Ambient studio light */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_18%,rgba(56,189,248,0.20),transparent_55%),radial-gradient(120%_90%_at_50%_100%,rgba(37,99,235,0.16),transparent_55%)]" />
      {/* Soft edge vignette for depth */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_120%_at_50%_50%,transparent_58%,rgba(0,0,0,0.55))]" />

      <div ref={stageRef} className="relative flex flex-col items-center px-8">
        {/* Breathing glow behind the mark */}
        <div
          ref={glowWrapRef}
          className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2"
          style={{ opacity: 0 }}
          aria-hidden
        >
          <div
            ref={glowPulseRef}
            className="h-44 w-[min(72vw,380px)] rounded-full bg-sky-500/25 blur-3xl"
          />
        </div>

        <div
          ref={logoRef}
          className="relative w-[min(66vw,260px)] [&_svg]:h-auto [&_svg]:w-full [&_svg_path]:fill-white"
          style={{ opacity: 0 }}
          dangerouslySetInnerHTML={{ __html: whiteLogo }}
        />

        {/* Slim progress bar — fills from the center outward */}
        <div
          ref={trackRef}
          className="mt-8 h-[3px] w-[min(52vw,190px)] overflow-hidden rounded-full bg-white/10"
          style={{ opacity: 0 }}
          aria-hidden
        >
          <div
            ref={fillRef}
            className="h-full w-full origin-center rounded-full bg-gradient-to-r from-sky-400 via-cyan-300 to-blue-500"
            style={{ transform: 'scaleX(0)' }}
          />
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;
